import type { AppLanguage } from '../i18n';

const naturalVoiceHint = /natural|neural|online|enhanced|premium|google|microsoft|aria|guy|jenny|sonia|ryan|davis|prabhat|gadis|andika/i;

const pronunciationByLanguage: Record<AppLanguage, Array<[RegExp, string]>> = {
  id: [
    [/AmertaSala/gi, 'Amerta Sala'],
    [/Peta\s*3D/gi, 'Peta tiga dimensi'],
    [/\b3D\b/gi, 'tiga dimensi'],
    [/\bXP\b/gi, 'eks pi'],
    [/\bkm\b/gi, 'kilometer'],
    [/\bRp\.?\s*/gi, 'rupiah '],
    [/&/g, ' dan '],
    [/Ngarsopuro/gi, 'Ngarso puro'],
    [/Mangkunegaran/gi, 'Mangku negaran'],
    [/Laweyan/gi, 'Lawe yan'],
    [/Srawung/gi, 'Sra wung'],
  ],
  en: [
    [/AmertaSala/gi, 'Amerta Sala'],
    [/Peta\s*3D/gi, 'three D map'],
    [/\b3D\b/gi, 'three D'],
    [/\bXP\b/gi, 'experience points'],
    [/\bkm\b/gi, 'kilometres'],
    [/\bRp\.?\s*/gi, 'Indonesian rupiah '],
    [/&/g, ' and '],
    [/Ngarsopuro/gi, 'Ngarso puro'],
    [/Mangkunegaran/gi, 'Mangku negaran'],
    [/Laweyan/gi, 'Lawe yan'],
    [/Srawung/gi, 'Sra wung'],
  ],
};

export function normalizeNarration(text: string, language: AppLanguage) {
  const withPronunciation = pronunciationByLanguage[language].reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    text,
  );

  return withPronunciation
    .replace(/\s*·\s*/g, '. ')
    .replace(/\s*—\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/\.\s*\./g, '.')
    .trim();
}

export function splitNarration(text: string, maxLength = 210) {
  const normalized = text.trim();
  if (!normalized) return [];

  const sentences = normalized.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [normalized];
  const chunks: string[] = [];
  let current = '';

  for (const rawSentence of sentences) {
    const sentence = rawSentence.trim();
    if (!sentence) continue;
    const candidate = `${current} ${sentence}`.trim();

    if (candidate.length > maxLength && current) {
      chunks.push(current);
      current = sentence;
    } else {
      current = candidate;
    }
  }

  if (current) chunks.push(current);
  return chunks;
}

export function scoreVoice(voice: SpeechSynthesisVoice, language: AppLanguage) {
  const locale = voice.lang.toLowerCase();
  const exactLocales = language === 'id' ? ['id-id'] : ['en-us', 'en-gb', 'en-au'];
  const prefix = language === 'id' ? 'id' : 'en';
  let score = 0;

  if (exactLocales.includes(locale)) score += 120;
  if (locale.startsWith(prefix)) score += 60;
  if (naturalVoiceHint.test(voice.name)) score += 32;
  if (!voice.localService) score += 12;
  if (voice.default) score += 6;

  return score;
}

export function sortVoices(voices: SpeechSynthesisVoice[], language: AppLanguage) {
  const prefix = language === 'id' ? 'id' : 'en';
  const matching = voices.filter((voice) => voice.lang.toLowerCase().startsWith(prefix));
  const pool = matching.length ? matching : voices;
  return [...pool].sort((a, b) => scoreVoice(b, language) - scoreVoice(a, language));
}
